import * as UserApi from "Data/API/User";
import { PayloadAction } from "@reduxjs/toolkit";
import { createAppSlice } from "Data/Redux/Helpers";
import { UserRoles, UserStatus } from "Data/Objects/UI";
import { IApiUser, IFilters, IUser, UsersState } from "Interfaces";
import {
	createPaginatedItem, deletePaginatedItem, getPaginatedData, getPaginatedDataFetchable, getPaginatedDataOptions,
	handleDataPage, updatePaginatedItem
} from "Data/Utils/Redux";

const initialState: UsersState = {
	data: [],
	lastPage: -1,
	options: {
		"page[size]": 15,
		"page[number]": 1
	},
	user: null,
	token: null
};

// # region Async Functions

const fetchNextUserPage = async (payload: void, thunkApi) => {
	const { users } = thunkApi.getState();
	const { token } = users;

	return UserApi.getUsers(token, getPaginatedDataOptions(users));
};

const createUserAsync = async (payload: IUser, thunkApi) => {
	const { users: { token } } = thunkApi.getState();

	return UserApi.createUser(payload, token);
};

const updateUserAsync = async (payload: IUser, thunkApi) => {
	const { users: { token } } = thunkApi.getState();

	return UserApi.updateUser(payload, token);
};

const deleteUserAsync = async (payload: string, thunkApi) => {
	const { users: { token } } = thunkApi.getState();

	return UserApi.deleteUser(payload, token);
};

const getUserAsync = async (payload: string, thunkApi) => {
	const { users: { token } } = thunkApi.getState();

	return UserApi.getUser(payload, token);
};

// # endregion

// # region Reducers

export const loginUserReducer = (state: UsersState, action: PayloadAction<IApiUser>) => {
	const { user, accessToken } = action?.payload ?? {};

	if (user && accessToken) {
		state.user = user;
		state.token = accessToken;
	}
};

export const logoutUserReducer = () => {
	return initialState;
};

export const handleUpdateUserReducer = (state: UsersState, action: PayloadAction<IUser>) => {
	updatePaginatedItem(state, action);

	if (action.payload?.id === state.user.id) {
		state.user = action.payload;
	}
};

// # endregion

// # region Selectors

const getUser = (state: UsersState) => {
	return state.user;
};

const getToken = (state: UsersState) => {
	return state.token;
};

const getUsersFiltered = (state: UsersState, filters: IFilters) => {
	const { USER_ROLE, USER_STATUS } = filters;
	const isAdmin = USER_ROLE === UserRoles.ADMIN;
	const onlyActive = USER_STATUS === UserStatus.ACTIVE;

	return state.data.filter(user => {
		return user.admin === isAdmin && (onlyActive ? user.active : true);
	});
};

// # endregion

const usersSlice = createAppSlice({
	name: "users",
	initialState,
	reducers: (builder) => ({
		createUserAction: builder.asyncThunk(createUserAsync, {
			fulfilled: createPaginatedItem
		}),
		updateUserAction: builder.asyncThunk(updateUserAsync, {
			fulfilled: updatePaginatedItem
		}),
		fetchNextUserPageAction: builder.asyncThunk(fetchNextUserPage, {
			fulfilled: handleDataPage
		}),
		getUserAction: builder.asyncThunk(getUserAsync, {
			fulfilled: updatePaginatedItem
		}),
		deleteUserAction: builder.asyncThunk(deleteUserAsync, {
			fulfilled: deletePaginatedItem
		}),
		loginUserAction: builder.asyncThunk(UserApi.loginUser, {
			fulfilled: loginUserReducer
		}),
		createUserPusherAction: builder.reducer(createPaginatedItem),
		updateUserPusherAction: builder.reducer(updatePaginatedItem),
		logoutUserAction: builder.reducer(logoutUserReducer)
	}),
	selectors: {
		selectUsers: getPaginatedData,
		selectUser: getUser,
		selectToken: getToken,
		selectUsersFetchable: getPaginatedDataFetchable,
		selectUsersOptions: getPaginatedDataOptions,
		selectFilteredUsers: getUsersFiltered
	}
});

export default usersSlice;

export const {
	createUserAction, createUserPusherAction, deleteUserAction, fetchNextUserPageAction, getUserAction,
	updateUserAction, updateUserPusherAction, logoutUserAction, loginUserAction
} = usersSlice.actions;

export const {
	selectFilteredUsers, selectToken, selectUser, selectUsers, selectUsersFetchable, selectUsersOptions
} = usersSlice.selectors;
